import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Lock,
  MagnifyingGlass,
  DownloadSimple,
  SignOut,
  ArrowSquareOut,
  EnvelopeSimple,
  Phone,
  X,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gs_admin_token";

const inputClass =
  "w-full bg-[#02060D] border border-white/10 text-white text-sm px-4 py-3 rounded-none focus:border-[#D1B677] focus:outline-none focus:ring-1 focus:ring-[#D1B677] placeholder:text-[#A1AEBF]/40 transition-colors";

const LoginGate = ({ onAuth }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Welcome back.");
      onAuth(res.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-gate"
      className="min-h-screen bg-[#02060D] flex items-center justify-center px-6"
    >
      <Toaster theme="dark" position="top-right" richColors />
      <div className="w-full max-w-md">
        <a
          href="/"
          className="inline-flex items-center gap-3 mb-12"
          data-testid="admin-logo"
        >
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_goa-invest/artifacts/mrr3mszg_17F6A14A-42EB-47E8-9DD4-22DA51DBF45F.PNG"
            alt="CountGoa"
            className="h-16 w-auto object-contain"
          />
        </a>

        <div className="border border-white/8 bg-[#050B14] p-8 lg:p-12">
          <div className="flex items-center gap-3 mb-3">
            <Lock size={20} weight="light" className="text-[#D1B677]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677]">
              Restricted Access
            </span>
          </div>
          <h1 className="font-serif text-white text-3xl mb-8 leading-snug">
            Lead Concierge Dashboard.
          </h1>

          <form onSubmit={submit} className="space-y-5" noValidate>
            <label className="block">
              <span className="block text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF] mb-2">
                Admin Password
              </span>
              <input
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••"
                className={inputClass}
                data-testid="admin-password-input"
                autoFocus
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full px-7 py-4 bg-[#D1B677] text-[#02060D] text-sm font-semibold hover:bg-[#E6CC8A] transition-all disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Enter Dashboard"}
            </button>
          </form>
        </div>

        <p className="text-xs text-[#A1AEBF]/60 mt-8 text-center">
          Authorized personnel only · Powering Goa · Connecting the World
        </p>
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="border border-white/8 bg-[#050B14] p-6 lg:p-8"
  >
    <div className="text-xs tracking-[0.25em] uppercase text-[#A1AEBF] mb-3">
      {label}
    </div>
    <div className="font-serif text-[#D1B677] text-4xl lg:text-5xl leading-none">
      {value}
    </div>
  </div>
);

const LeadDrawer = ({ lead, onClose }) => {
  if (!lead) return null;
  const isBusiness = lead.type === "business";
  const fields = isBusiness
    ? [
        ["Business Name", lead.business_name],
        ["Contact Person", lead.contact_person],
        ["Email", lead.email],
        ["Phone", lead.phone],
        ["Category", lead.category],
        ["City", lead.city],
        ["Website", lead.website],
        ["Description", lead.description],
      ]
    : [
        ["Full Name", lead.full_name],
        ["Organization", lead.organization],
        ["Email", lead.email],
        ["Phone", lead.phone],
        ["Country", lead.country],
        ["Intent", lead.interest],
        ["Sectors", Array.isArray(lead.sectors) ? lead.sectors.join(", ") : ""],
        ["Ticket Size", lead.ticket_size],
        ["Message", lead.message],
      ];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <div
        className="absolute inset-0 bg-[#02060D]/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative ml-auto w-full max-w-xl bg-[#050B14] border-l border-white/10 h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#050B14] border-b border-white/8 p-6 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D1B677] mb-2">
              {isBusiness ? "Goa Business · FREE" : "Investor / Partner"}
            </div>
            <div className="font-serif text-white text-2xl leading-tight">
              {isBusiness ? lead.business_name : lead.full_name}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="lead-drawer-close"
            className="text-[#A1AEBF] hover:text-white p-1"
          >
            <X size={22} weight="light" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            {lead.email && (
              <a
                href={`mailto:${lead.email}`}
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#D1B677] text-[#D1B677] text-xs hover:bg-[#D1B677] hover:text-[#02060D] transition-all"
              >
                <EnvelopeSimple size={16} weight="light" />
                Reply
              </a>
            )}
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white text-xs hover:border-white/40 transition-all"
              >
                <Phone size={16} weight="light" />
                Call
              </a>
            )}
            {lead.website && (
              <a
                href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white text-xs hover:border-white/40 transition-all"
              >
                <ArrowSquareOut size={16} weight="light" />
                Website
              </a>
            )}
          </div>

          <div className="border-t border-white/8 pt-4">
            {fields.map(([label, val]) => {
              if (!val) return null;
              return (
                <div
                  key={label}
                  className="py-3 border-b border-white/5 grid grid-cols-3 gap-4"
                >
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF] col-span-1 pt-1">
                    {label}
                  </div>
                  <div className="text-sm text-white col-span-2 break-words">
                    {val}
                  </div>
                </div>
              );
            })}
            <div className="py-3 grid grid-cols-3 gap-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF] col-span-1 pt-1">
                Submitted
              </div>
              <div className="text-xs text-[#A1AEBF] col-span-2">
                {new Date(lead.created_at).toLocaleString()}
              </div>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF] col-span-1 pt-1">
                Lead ID
              </div>
              <div className="text-[11px] text-[#A1AEBF]/70 col-span-2 font-mono break-all">
                {lead.id}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (type !== "all") params.type = type;
      if (search) params.search = search;
      const [res, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, {
          headers: { "X-Admin-Token": token },
          params,
        }),
        axios.get(`${API}/stats`),
      ]);
      setItems(res.data.items || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const exportCsv = () => {
    const qs = new URLSearchParams({ token });
    if (type !== "all") qs.set("type", type);
    window.open(`${API}/admin/leads/export.csv?${qs.toString()}`, "_blank");
  };

  const displayName = (l) =>
    l.type === "business" ? l.business_name : l.full_name;
  const displayMeta = (l) =>
    l.type === "business"
      ? `${l.category || "—"} · ${l.city || "Goa"}`
      : `${l.interest || "—"} · ${l.country || "—"}${l.organization ? " · " + l.organization : ""}`;

  const rows = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-[#02060D]" data-testid="admin-dashboard">
      <Toaster theme="dark" position="top-right" richColors />
      <LeadDrawer lead={selected} onClose={() => setSelected(null)} />

      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-[#02060D]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_goa-invest/artifacts/mrr3mszg_17F6A14A-42EB-47E8-9DD4-22DA51DBF45F.PNG"
              alt="CountGoa"
              className="h-12 w-auto object-contain"
            />
            <span className="hidden sm:inline-block text-[10px] tracking-[0.3em] uppercase text-[#A1AEBF]/70 border-l border-white/10 pl-3 ml-1">
              Lead Concierge
            </span>
          </a>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white text-xs hover:border-white/40 transition-all"
          >
            <SignOut size={16} weight="light" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
            Inbound Opportunities
          </span>
        </div>
        <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-12">
          Lead Concierge.{" "}
          <span className="italic text-[#D1B677]">Goa to the world.</span>
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          <Stat
            label="Total Leads"
            value={stats?.total_leads ?? "—"}
            testid="stat-total"
          />
          <Stat
            label="Goa Businesses · FREE"
            value={stats?.business_leads ?? "—"}
            testid="stat-business"
          />
          <Stat
            label="Investors · Partners"
            value={stats?.investor_leads ?? "—"}
            testid="stat-investor"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="flex border border-white/10">
            {[
              { k: "all", l: "All" },
              { k: "business", l: "Businesses" },
              { k: "investor", l: "Investors" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setType(t.k)}
                data-testid={`filter-${t.k}`}
                className={`px-5 py-3 text-xs tracking-[0.15em] uppercase transition-colors ${
                  type === t.k
                    ? "bg-[#D1B677] text-[#02060D]"
                    : "text-[#A1AEBF] hover:text-white"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>

          <form onSubmit={onSearch} className="flex-1 flex gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass
                size={18}
                weight="light"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1AEBF]"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, category, country..."
                className={`${inputClass} pl-11`}
                data-testid="admin-search-input"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-search-btn"
              className="px-5 py-3 border border-white/15 text-white text-xs hover:border-[#D1B677] hover:text-[#D1B677] transition-all"
            >
              Search
            </button>
          </form>

          <button
            onClick={exportCsv}
            data-testid="admin-export-btn"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#D1B677] text-[#02060D] text-xs font-semibold hover:bg-[#E6CC8A] transition-all"
          >
            <DownloadSimple size={16} weight="bold" />
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/8 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#050B14] border-b border-white/8 text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF]">
            <div className="col-span-1">Type</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-4">Detail</div>
            <div className="col-span-2">Contact</div>
            <div className="col-span-2 text-right">Received</div>
          </div>

          {loading && (
            <div className="px-6 py-16 text-center text-[#A1AEBF] text-sm">
              Loading...
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div
              data-testid="admin-empty"
              className="px-6 py-20 text-center"
            >
              <div className="font-serif text-white text-2xl mb-2">No leads yet.</div>
              <div className="text-sm text-[#A1AEBF]">
                As soon as someone registers on CountGoa, they'll appear here.
              </div>
            </div>
          )}

          {!loading &&
            rows.map((l, i) => (
              <button
                key={l.id || i}
                onClick={() => setSelected(l)}
                data-testid={`lead-row-${i}`}
                className="w-full grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 last:border-b-0 hover:bg-[#0C5A48]/10 transition-colors text-left items-center"
              >
                <div className="col-span-1">
                  <span
                    className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 border ${
                      l.type === "business"
                        ? "border-[#D1B677]/50 text-[#D1B677]"
                        : "border-[#14856A]/60 text-[#14856A]"
                    }`}
                  >
                    {l.type === "business" ? "Biz" : "Inv"}
                  </span>
                </div>
                <div className="col-span-3">
                  <div className="font-serif text-white text-lg leading-tight">
                    {displayName(l)}
                  </div>
                </div>
                <div className="col-span-4 text-sm text-[#A1AEBF]">
                  {displayMeta(l)}
                </div>
                <div className="col-span-2 text-xs text-[#A1AEBF] truncate">
                  {l.email}
                </div>
                <div className="col-span-2 text-xs text-[#A1AEBF] text-right">
                  {l.created_at
                    ? new Date(l.created_at).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </button>
            ))}
        </div>
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginGate onAuth={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
